/**
 * Valentine Mails Bidirectionnelle
 * Fred voit tout : brouillons de réponse préparés par Valentine → valide/édite/rejette → envoi auto.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Mail, MailCheck, MailX, Loader2, Send, RefreshCw, Edit, Check, X,
  Sparkles, Clock, Flame, AlertCircle, Inbox, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const htmlToText = (html) => {
  if (!/<(p|br|ul|li|b|div)\b/i.test(html || '')) return html || '';
  return html
    .replace(/\s*\n\s*/g, ' ')
    .replace(/\s*<br\s*\/?>\s*/gi, '\n')
    .replace(/\s*<\/(p|ul|div)>\s*/gi, '\n\n')
    .replace(/\s*<\/li>\s*/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};


const URGENCE_META = {
  haute: { label: 'Haute', color: 'bg-red-100 text-red-900 border-red-300', icon: Flame },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-900 border-blue-300', icon: Clock },
  basse: { label: 'Basse', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock },
};

export default function ValentineMailsPage() {
  const [briefing, setBriefing] = useState(null);
  const [brouillons, setBrouillons] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastScan, setLastScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filtre, setFiltre] = useState('en_attente');
  const [selected, setSelected] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editSujet, setEditSujet] = useState('');
  const [editCorps, setEditCorps] = useState('');
  const [sending, setSending] = useState(false);

  const loadAll = async () => {
    try {
      const [br, bf] = await Promise.all([
        api.get(`/valentine-mails/pending?statut=${filtre}`),
        api.get('/valentine-mails/briefing-matin'),
      ]);
      setBrouillons(br.data.brouillons || []);
      setCounts(br.data.counts || {});
      setLastScan(br.data.last_scan_at);
      setBriefing(bf.data);
    } catch (e) {
      toast.error('Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [filtre]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post('/valentine-mails/scan', { jours: 7, inclure_lus: true });
      toast.success(`${r.data.total_brouillons} brouillons créés — ${r.data.scannes.length} boîtes scannées`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan impossible');
    } finally {
      setScanning(false);
    }
  };

  const approuver = async (b) => {
    if (!window.confirm(`Envoyer la réponse à ${b.to_email || b.from_email_clean || b.from_mail} ?\n\n${htmlToText(b.corps_reply).slice(0, 200)}...`)) return;
    setSending(true);
    try {
      await api.post('/valentine-mails/approuver', { brouillon_id: b.id });
      toast.success('Email envoyé');
      setSelected(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi échoué');
    } finally { setSending(false); }
  };

  const rejeter = async (b) => {
    const raison = window.prompt('Raison du rejet (optionnel) :', '');
    try {
      await api.post('/valentine-mails/rejeter', { brouillon_id: b.id, raison });
      toast.success('Brouillon rejeté');
      await loadAll();
    } catch { toast.error('Rejet impossible'); }
  };

  const regenerer = async (b) => {
    toast.info('Valentine reformule…');
    try {
      await api.post(`/valentine-mails/regenerer-brouillon/${b.id}`);
      toast.success('Nouveau brouillon prêt');
      await loadAll();
      if (selected?.id === b.id) {
        const r = await api.get(`/valentine-mails/brouillon/${b.id}`);
        setSelected(r.data);
      }
    } catch { toast.error('Reformulation échouée'); }
  };

  const openEdit = (b) => {
    setSelected(b);
    setEditSujet(b.sujet_reply || `Re: ${b.sujet_recu}`);
    setEditCorps(b.corps_reply || '');
    setShowEditDialog(true);
  };

  const saveEdit = async () => {
    try {
      await api.post('/valentine-mails/editer', {
        brouillon_id: selected.id,
        reply_body: editCorps,
        reply_subject: editSujet,
      });
      toast.success('Brouillon enregistré');
      setShowEditDialog(false);
      await loadAll();
    } catch { toast.error('Enregistrement impossible'); }
  };

  const saveAndSend = async () => {
    setSending(true);
    try {
      await api.post('/valentine-mails/approuver', {
        brouillon_id: selected.id,
        reply_body: editCorps,
      });
      toast.success('Email envoyé');
      setShowEditDialog(false);
      setSelected(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi échoué');
    } finally { setSending(false); }
  };

  const speak = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = 1.05;
    const voices = window.speechSynthesis.getVoices();
    const fr = voices.find(v => v.lang.startsWith('fr'));
    if (fr) u.voice = fr;
    window.speechSynthesis.speak(u);
  };

  if (loading) return <div className="p-6 flex gap-2 items-center text-sm"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;

  return (
    <div className="p-4 space-y-4" data-testid="valentine-mails-page">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Mail className="w-7 h-7 text-emerald-700" />
            Valentine Mails
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Elle scanne tes boîtes Gmail, détecte les emails sans réponse, et te prépare les brouillons à valider.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {lastScan && <span className="text-xs text-muted-foreground">Dernier scan : {new Date(lastScan).toLocaleString('fr-FR')}</span>}
          <Button onClick={scan} disabled={scanning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-scan-mails">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Scanner maintenant
          </Button>
        </div>
      </div>

      {/* Briefing matin */}
      {briefing && briefing.nb_total_a_valider > 0 && (
        <Card className="border-emerald-500 border-2 bg-gradient-to-br from-emerald-50 to-white" data-testid="briefing-matin">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">{briefing.message}</div>
                <button onClick={() => speak(briefing.message)} className="ml-2 opacity-60 hover:opacity-100" title="Écouter"><Volume2 className="w-3 h-3 inline" /></button>
                {briefing.par_societe && Object.keys(briefing.par_societe).length > 0 && (
                  <div className="mt-2 text-xs space-y-1">
                    {Object.entries(briefing.par_societe).map(([soc, urgs]) => {
                      const total = (urgs.haute?.length || 0) + (urgs.normale?.length || 0) + (urgs.basse?.length || 0);
                      if (!total) return null;
                      return (
                        <div key={soc}>
                          <strong>{soc}</strong> — {total} email{total > 1 ? 's' : ''}
                          {urgs.haute?.length > 0 && <span className="ml-2 text-red-700 font-semibold">· {urgs.haute.length} urgent{urgs.haute.length > 1 ? 's' : ''}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { k: 'en_attente', l: 'À valider', color: 'text-emerald-700', icon: Inbox },
          { k: 'ignore_ia', l: 'Ignorés IA', color: 'text-gray-600', icon: MailX },
          { k: 'approuve', l: 'Approuvés', color: 'text-blue-700', icon: Check },
          { k: 'envoye', l: 'Envoyés', color: 'text-emerald-900', icon: MailCheck },
          { k: 'rejete', l: 'Rejetés', color: 'text-red-700', icon: X },
        ].map(({ k, l, color, icon: Icon }) => (
          <button key={k} onClick={() => setFiltre(k)} className={`text-left p-3 rounded-lg border transition-all ${filtre === k ? 'border-emerald-500 bg-emerald-50 shadow' : 'border-border hover:bg-muted/50'}`} data-testid={`filter-${k}`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="w-3 h-3" />{l}</div>
            <div className={`text-2xl font-bold ${color}`}>{counts[k] || 0}</div>
          </button>
        ))}
      </div>

      {/* Liste brouillons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Panel gauche : liste */}
        <Card className="max-h-[70vh] overflow-y-auto">
          <CardContent className="p-2 space-y-1">
            {brouillons.length === 0 && (
              <p className="text-sm text-muted-foreground italic text-center py-8">
                {filtre === 'en_attente' ? '✨ Boîte à zéro ! Rien à valider.' : 'Aucun brouillon dans cette catégorie.'}
              </p>
            )}
            {brouillons.map(b => {
              const urg = URGENCE_META[b.urgence] || URGENCE_META.normale;
              const UrgIcon = urg.icon;
              const isSelected = selected?.id === b.id;
              return (
                <button
                  key={b.id}
                  onClick={() => setSelected(b)}
                  className={`w-full text-left p-3 rounded border transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-muted/40 border-border'}`}
                  data-testid={`brouillon-${b.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <Badge className={urg.color} variant="outline"><UrgIcon className="w-3 h-3 mr-1" />{urg.label}</Badge>
                    <span className="text-xs text-muted-foreground truncate">{b.societe_nom}</span>
                  </div>
                  <div className="font-semibold text-sm truncate">{b.sujet_recu}</div>
                  <div className="text-xs text-muted-foreground truncate">De : {b.from_mail}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{(b.internal_date || '').slice(0, 16).replace('T', ' ')}</div>
                  {b.notes_pour_fred && (
                    <div className="mt-1 text-xs italic text-amber-700">💡 {b.notes_pour_fred}</div>
                  )}
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Panel droite : détail / édition */}
        <Card className="max-h-[70vh] overflow-y-auto">
          <CardContent className="p-3 space-y-3">
            {!selected && (
              <p className="text-sm text-muted-foreground italic text-center py-10">
                Sélectionne un brouillon dans la liste pour voir le détail.
              </p>
            )}
            {selected && (
              <>
                <div className="border-b pb-2">
                  <div className="font-semibold text-sm">{selected.sujet_recu}</div>
                  <div className="text-xs text-muted-foreground">De : {selected.from_mail}</div>
                  <div className="text-xs font-medium text-emerald-800" data-testid="destinataire-reponse">Réponse envoyée à : {selected.to_email || selected.from_email_clean || selected.from_mail}</div>
                  <div className="text-xs text-muted-foreground">Reçu : {(selected.internal_date || '').slice(0, 16).replace('T', ' ')}</div>
                  <div className="text-xs text-muted-foreground">Thread : {selected.nb_msgs_thread} message{selected.nb_msgs_thread > 1 ? 's' : ''}</div>
                </div>

                <details className="text-xs">
                  <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">📬 Lire le mail reçu</summary>
                  <div className="mt-2 p-2 bg-muted/40 rounded text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">{selected.corps_recu || '(vide)'}</div>
                </details>

                {selected.notes_pour_fred && (
                  <div className="text-xs italic text-amber-700 bg-amber-50 p-2 rounded">💡 {selected.notes_pour_fred}</div>
                )}

                {selected.doit_repondre === false && (
                  <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded border border-gray-300">
                    ⚠️ Valentine suggère de NE PAS répondre — {selected.analyse_ia?.raison || 'motif non précisé'}
                  </div>
                )}

                <div>
                  <Label className="text-xs">Sujet de la réponse</Label>
                  <div className="font-medium text-sm">{selected.sujet_reply}</div>
                </div>

                <div>
                  <Label className="text-xs">Brouillon de réponse (Valentine)</Label>
                  <div className="mt-1 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm whitespace-pre-wrap min-h-[100px]">
                    {htmlToText(selected.corps_reply) || <span className="italic text-muted-foreground">(aucun brouillon)</span>}
                  </div>
                </div>

                {selected.statut === 'en_attente' && (
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => approuver(selected)} disabled={sending || !selected.corps_reply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-approuver">
                      {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Envoyer tel quel
                    </Button>
                    <Button onClick={() => openEdit(selected)} variant="outline" data-testid="btn-editer"><Edit className="w-4 h-4 mr-2" />Éditer</Button>
                    <Button onClick={() => regenerer(selected)} variant="ghost" data-testid="btn-regenerer"><Sparkles className="w-4 h-4 mr-2" />Reformule</Button>
                    <Button onClick={() => rejeter(selected)} variant="ghost" className="text-red-700 hover:text-red-900 ml-auto" data-testid="btn-rejeter"><X className="w-4 h-4 mr-2" />Rejeter</Button>
                  </div>
                )}
                {selected.statut === 'envoye' && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded flex items-center gap-2"><MailCheck className="w-4 h-4" />Envoyé le {(selected.sent_at || '').slice(0, 16).replace('T', ' ')}</div>
                )}
                {selected.statut === 'rejete' && (
                  <div className="text-xs text-red-700 bg-red-50 p-2 rounded">❌ Rejeté{selected.raison_rejet ? ` — ${selected.raison_rejet}` : ''}</div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog édition */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Éditer le brouillon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Sujet</Label>
              <Input value={editSujet} onChange={(e) => setEditSujet(e.target.value)} data-testid="edit-sujet" />
            </div>
            <div>
              <Label className="text-xs">Corps</Label>
              <Textarea value={editCorps} onChange={(e) => setEditCorps(e.target.value)} rows={12} className="font-mono text-sm" data-testid="edit-corps" />
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button variant="outline" onClick={saveEdit} data-testid="btn-save-edit"><Edit className="w-4 h-4 mr-2" />Enregistrer sans envoyer</Button>
            <Button onClick={saveAndSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-send">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enregistrer & Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
